/* eslint-disable */
import {
    Chip,
    Link,
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button
} from '@nextui-org/react'
import { useEffect, useRef, useState } from 'react'
import { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AcademicAdmissionFunctions } from './AcademicAdmissionFunctions'

import { AlertModal, Tips, ContainerGroupField } from '@/components/common'
import { LittleText, Text } from '@/components/common/UI'
import {
    AutocompleteControl,
    SelectControl,
    CheckboxControl
} from '@/components/form-controls'
import { Icons } from '@/components/icons'
import { useAcademicOfferingValidation } from '@/hooks/useAcademicOfferingValidation'
import { useMountedEffect } from '@/hooks/useMountedEffect'
import { useProviderLoader } from '@/Providers/useProviderLoader'
import { useResourcesApi } from '@/services/api/resourcesApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { useStore } from '@/store'
import { MessageLevel, ErrorHandlingStrategy } from '@/types/api'
import {
    AcademicAdmission,
    OptionsPrograms,
    OptionsField,
    AcademicCycleWithInitialDate
} from '@/types/forms/'
import { ModalTypesEntry } from './ModalTypesEntry'
import { CategoriaOption, mapSapCategoriasToOptions } from '@/types'

interface EntryControlProps {
    control: Control<AcademicAdmission>
    watchProgram: string
    watchAdmissionType: string
    watchJornada: string
    watchCampus: string
    watchStartPeriod: string
    watchEducationModality: string
    setValue: UseFormSetValue<AcademicAdmission>
    idAspirant?: number | null
    preloadedProgram?: string
}

// Helper function to check if admission type requires validation
const requiresValidation = (admissionType: string): boolean => {
    return (
        admissionType === '02' ||
        admissionType === '03' ||
        admissionType === '04'
    )
}

// Helper function to create validation payload
const createValidationPayload = (
    programId: string,
    admissionTypeId: string,
    campusId: string,
    jornadaId: string,
    cycleId?: string
) => ({
    programId,
    admissionTypeId,
    campusId,
    jornadaId,
    cycleId
})

// Helper function to clear related fields
const clearRelatedFields = (
    setValue: UseFormSetValue<AcademicAdmission>,
    fields: Array<
        'program' | 'admissionType' | 'studyModality' | 'startPeriod' | 'campus'
    >
) => {
    fields.forEach((field) => setValue(field, ''))
}

const formatDateToDDMMYYYY = (dateStr: string): string => {
    if (!dateStr) return dateStr
    // yyyy/mm/dd o yyyy-mm-dd → dd/mm/yyyy
    const isoMatch = dateStr.match(/^(\d{4})[\/\-](\d{2})[\/\-](\d{2})$/)
    if (isoMatch) return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`
    // dd/mm/yyyy o dd-mm-yyyy → ya está en formato correcto o normalizar separador
    const dmyMatch = dateStr.match(/^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/)
    if (dmyMatch) return `${dmyMatch[1]}/${dmyMatch[2]}/${dmyMatch[3]}`
    return dateStr
}

export const EntryControl = ({
    control,
    watchProgram,
    watchAdmissionType,
    watchJornada,
    watchCampus,
    watchStartPeriod,
    watchEducationModality,
    setValue,
    idAspirant,
    preloadedProgram
}: EntryControlProps): JSX.Element => {
    const { t } = useTranslation()
    const text = 'academicAdmission'

    // Data states
    const [programs, setPrograms] = useState<OptionsPrograms[]>([])
    const [academicModalities, setAcademicModalities] = useState<
        OptionsField[]
    >([])
    const [filteredPrograms, setFilteredPrograms] = useState<OptionsPrograms[]>(
        []
    )
    const [admissionTypes, setAdmissionTypes] = useState<OptionsField[]>([])
    const [campus, setCampus] = useState<OptionsField[]>([])
    const [jornadas, setJornadas] = useState<OptionsField[]>([])
    const [academicCycle, setAcademicCycle] = useState<
        AcademicCycleWithInitialDate[]
    >([])
    const [isInstructiveModalOpen, setIsInstructiveModalOpen] = useState(false)
    const [hasOpenedInstructiveModal, setHasOpenedInstructiveModal] =
        useState(false)
    const [isModalTypesEntryOpen, setIsModalTypesEntryOpen] = useState(false)
    const [showRejectedHistoryModal, setShowRejectedHistoryModal] = useState(false)

    // URL oficial del instructivo de la universidad

    const INSTRUCTIVE_PDF_URL = import.meta.env.VITE_URL_ACUERDO;
    /* const [categoriasAdmision, setCategoriasAdmision] = useState<
        CategoriaOption[]
    >([]) */

    const {
        fetchPrograms,
        fetchAdmissionTypes,
        fetchJornada,
        fetchAcademicCycleWithInitialDate,
        fetchCampusByProgram,
        colorChip,
        isLoading,
        isLoadingAdmissionTypes,
        isLoadingCampus,
        isLoadingJornada,
        isLoadingCycle
    } = AcademicAdmissionFunctions()

    const resourcesApi = useResourcesApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const { showLoader, hideLoader } = useProviderLoader()

    const setStartDate = useStore((state) => state.setStartDate)

    const {
        validateSelection,
        isValidating,
        showModal,
        setShowModal,
        modalMessage,
        modalTitle,
        resetModal
    } = useAcademicOfferingValidation()

    //* Programa precargado (URL / sessionStorage): no pasa por onBeforeChange,
    // así que se valida aquí doble línea y reintegro igual que al seleccionarlo
    const validatedPreloadRef = useRef<string | null>(null)

    useEffect(() => {
        if (
            !idAspirant ||
            !preloadedProgram ||
            watchProgram !== preloadedProgram ||
            validatedPreloadRef.current === preloadedProgram
        ) {
            return
        }

        validatedPreloadRef.current = preloadedProgram

        const validatePreloadedProgram = async (): Promise<void> => {
            const isValid = await validateSelection(
                createValidationPayload(preloadedProgram, '', '', '')
            )

            if (!isValid) {
                clearRelatedFields(setValue, [
                    'program',
                    'admissionType',
                    'campus',
                    'studyModality',
                    'startPeriod'
                ])
            }
        }

        validatePreloadedProgram()
    }, [idAspirant, preloadedProgram, watchProgram, validateSelection, setValue])

    //* Categorías de Admisión - Comentado temporalmente hasta que se necesite
   /*  useEffect(() => {
         let mounted = true
         const fetchCategoriasAdmision = async (): Promise<void> => {
             try {
                 const { data, status } =
                     await resourcesApi.getCategoriasAdmision()

                 if (mounted && status === 200) {
                     const options = mapSapCategoriasToOptions(data)
                      //Prepend "No Aplica" as empty string value
                     const withNoAplica = [
                         { label: 'No Aplica', value: '' },
                         ...options
                     ]

                     setCategoriasAdmision(withNoAplica)
                 }
             } catch (error) {
                 console.error('Error fetching categorias admision:', error)
             }
         }

         fetchCategoriasAdmision()

         return (): void => {
             mounted = false
         }
     }, []) */

    //* Program
    useMountedEffect(
        async (isMounted) => {
            if (!watchEducationModality) {
                return
            }

            const response = await fetchPrograms(watchEducationModality)

            if (
                isMounted() &&
                response &&
                Array.isArray(response) &&
                response.length > 0
            ) {
                setPrograms(response)
                setFilteredPrograms(response)
            }
        },
        [watchEducationModality]
    )

    //* Academic Modalities
    useEffect(() => {
        let mounted = true
        const fetchAcademicModalities = async (): Promise<void> => {
            try {
                const { data, status } =
                    await resourcesApi.getAcademicModalities()

                if (mounted && status === 200) {
                    // Opcionalmente filtrar modalidades por IDs permitidos desde .env
                    const filterEnabled =
                        import.meta.env.VITE_FILTER_MODALITIES_ENABLED ===
                        'true'
                    const allowedIds = (import.meta.env
                        .VITE_ALLOWED_MODALITY_IDS || '')
                        .split(',')
                        .map((v) => v.trim())
                        .filter(Boolean)

                    const filtered =
                        filterEnabled && allowedIds.length > 0
                            ? data.filter((item: any) =>
                                  allowedIds.includes(String(item.value))
                              )
                            : data

                    setAcademicModalities(filtered)
                }
            } catch (error) {
                console.error('Error fetching academic modalities:', error)
            }
        }

        fetchAcademicModalities()

        return (): void => {
            mounted = false
        }
    }, [])

    //* Filter programs by selected modality
    useEffect(() => {
        // Los programas ya vienen filtrados por modalityId del API,
        // así que simplemente mostramos todos los programas cargados
        if (programs.length > 0) {
            setFilteredPrograms(programs)
        }
    }, [programs])

    //* AdmissionType
    useMountedEffect(
        async (isMounted) => {
            if (!watchProgram) {
                return
            }

            const response = await fetchAdmissionTypes(watchProgram)

            if (isMounted() && response) {
                setAdmissionTypes(response)
            }
        },
        [watchProgram]
    )

    //* Campus
    useMountedEffect(
        async (isMounted) => {
            // Limpiar campus cuando cambian las dependencias
            setCampus([])
            setValue('campus', '')

            if (!watchProgram || !watchAdmissionType) {
                return
            }

            const response = await fetchCampusByProgram(
                watchProgram,
                watchAdmissionType
            )

            if (isMounted() && response) {
                setCampus(response)
            }
        },
        [watchProgram, watchAdmissionType]
    )

    //* Jornada
    useMountedEffect(
        async (isMounted) => {
            if (!watchProgram || !watchAdmissionType || !watchCampus) {
                return
            }

            const response = await fetchJornada(
                watchProgram,
                watchAdmissionType,
                watchCampus
            )

            if (isMounted() && response) {
                setJornadas(response)
            }
        },
        [watchProgram, watchAdmissionType, watchCampus]
    )

    //* Cycle
    useMountedEffect(
        async (isMounted) => {
            // Clear previous data when parameters change
            setAcademicCycle([])
            setValue('startPeriod', '')

            // Only fetch if all required parameters are available
            if (
                !watchProgram ||
                !watchAdmissionType ||
                !watchCampus ||
                !watchJornada
            ) {
                return
            }

            try {
                const response = await fetchAcademicCycleWithInitialDate(
                    Number(watchProgram),
                    watchAdmissionType,
                    Number(watchCampus),
                    Number(watchJornada)
                )

                if (isMounted() && response) {
                    setAcademicCycle(response)
                } else if (isMounted()) {
                    setAcademicCycle([])
                }
            } catch (error) {
                console.log(
                    'Error fetching academic cycle (404 - no dates available):',
                    error
                )
                if (isMounted()) {
                    setAcademicCycle([])
                    setValue('startPeriod', '')
                }
            }
        },
        [watchProgram, watchAdmissionType, watchCampus, watchJornada]
    )

    return (
        <ContainerGroupField tittleGroup={t('forms.groups.entry')}>
            <SelectControl
                name="educationModality"
                control={control}
                label={t(`${text}.programType`)}
                placeholder={t(`${text}.selectProgramType`)}
                options={academicModalities}
                value={watchEducationModality}
                //isDisabled={Boolean(watchEducationModality)} // TODO: Descomentar cuando se tenga el backend
                onChangeCustom={() => {
                    clearRelatedFields(setValue, [
                        'program',
                        'admissionType',
                        'studyModality',
                        'startPeriod'
                    ])
                }}
            />

            <AutocompleteControl
                onBeforeChange={async (value) => {
                    // SIEMPRE validar el programa (incluye validación SAP).
                    // Sin tipo de ingreso: al cambiar programa se limpia, y así
                    // siempre corre la validación de elegibilidad del programa
                    const payload = createValidationPayload(
                        value,
                        '',
                        watchCampus || '',
                        watchJornada || '',
                        watchStartPeriod || undefined
                    )

                    return await validateSelection(payload)
                }}
                onChangeCustom={() => {
                    clearRelatedFields(setValue, [
                        'admissionType',
                        'studyModality',
                        'startPeriod'
                    ])
                }}
                name="program"
                control={control}
                label={t(`${text}.program`)}
                placeholder={t(`${text}.program`)}
                options={filteredPrograms}
                customRender={(option) => (
                    <div className="flex flex-row gap-2 items-center">
                        <span>{option.label}</span>
                        <Chip
                            color={colorChip(option.modalityName)}
                            size="sm"
                        >
                            {option.modalityName}
                        </Chip>
                    </div>
                )}
                rules={{
                    required: t(`${text}.required.programRequired`)
                }}
                isLoading={isLoading || isValidating}
                className="w-full"
                isDisabled={!watchEducationModality || isValidating}
            />

            <div
                className="space-y-2 cursor-pointer"
                onClick={() => {
                    setIsModalTypesEntryOpen(true)
                }}
            >
                <div className="flex gap-2 items-center">
                    <LittleText
                        text={t(`${text}.modalTypesEntryTitle`)}
                        className="text-center text-primary"
                    />
                    <Icons.InfoCircle className="w-4 h-4 text-primary" />
                </div>
                <SelectControl
                    onBeforeChange={async (value) => {
                        if (requiresValidation(value) && watchProgram) {
                            const payload = createValidationPayload(
                                watchProgram,
                                value,
                                watchCampus || '',
                                watchJornada || '',
                                watchStartPeriod || undefined
                            )

                            return await validateSelection(payload)
                        }

                        return true
                    }}
                    onChangeCustom={() => {
                        clearRelatedFields(setValue, [
                            'campus',
                            'studyModality',
                            'startPeriod'
                        ])
                    }}
                    name="admissionType"
                    control={control}
                    label={t(`${text}.typeOfEntry`)}
                    placeholder={t(`${text}.typeOfEntry`)}
                    options={admissionTypes}
                    rules={{
                        required: t(`${text}.required.typeOfEntryRequired`)
                    }}
                    isDisabled={!watchProgram || isValidating}
                    isLoading={isLoadingAdmissionTypes || isValidating}
                />
            </div>

            {/* Mostrar indicador cuando se están cargando tipos de ingreso (solo cuando no se está validando) */}
            {isLoadingAdmissionTypes && watchProgram && !isValidating && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <p className="text-blue-800 text-sm">
                        Cargando tipos de ingreso disponibles...
                    </p>
                </div>
            )}

            <SelectControl
                onChangeCustom={() => {
                    setValue('studyModality', '')
                    setValue('startPeriod', '')
                }}
                name="campus"
                control={control}
                label={t(`${text}.sede`)}
                placeholder={t(`${text}.sede`)}
                options={campus}
                rules={{
                    required: t(`${text}.required.sedeRequired`)
                }}
                isDisabled={!watchAdmissionType}
                isLoading={isLoadingCampus}
                className="md:mt-6"
            />
            <SelectControl
                onChangeCustom={() => {
                    setValue('startPeriod', '')
                }}
                name="studyModality"
                control={control}
                label={t(`${text}.modality`)}
                placeholder={t(`${text}.modality`)}
                options={jornadas}
                rules={{
                    required: t(`${text}.required.modalityRequired`)
                }}
                isDisabled={!watchAdmissionType}
                isLoading={isLoadingJornada}
            />

            <AutocompleteControl
                name="startPeriod"
                control={control}
                label={t(`${text}.startDate`)}
                placeholder={
                    academicCycle.length === 0 && watchJornada
                        ? t(`${text}.noDateAvailable`)
                        : t(`${text}.startDate`)
                }
                options={academicCycle as any}
                labelKey="cycleName"
                valueKey="cycleId"
                customRender={(option) => (
                    <div className="flex flex-row gap-2">
                        <Text text={(option.cycleName || '').replace(/\s\d{3}\s/, ' ').trim()} />
                        <Chip color="primary">
                            {t(`${text}.startDateChip`)} {formatDateToDDMMYYYY(option.initialDate)}
                        </Chip>
                    </div>
                )}
                onChangeCustom={async (value) => {
                    // No establecer el valor aquí, ya se establece en onSelectionChange
                    // Solo actualizar la fecha de inicio si hay un ciclo seleccionado
                    const selected = academicCycle.find(
                        (cycle) => String(cycle.cycleId) === String(value)
                    )

                    if (selected) {
                        console.log('📅 Periodo de inicio seleccionado:', {
                            cycleId: selected.cycleId,
                            cycleName: selected.cycleName,
                            initialDate: selected.initialDate,
                            value: value
                        })
                        setStartDate(selected.initialDate)

                        // Si el ciclo NO requiere checkbox (4, 7, 15), eliminar el campo del formulario
                        // Si el ciclo SÍ requiere checkbox, resetearlo a false
                        const shouldShowCheckbox = ![4, 7, 15].includes(
                            selected.cycleId
                        )
                        setValue(
                            'acceptInstructiveDifferentPeriod',
                            shouldShowCheckbox ? false : undefined
                        )
                        setHasOpenedInstructiveModal(false)

                        // 👇 NUEVA VALIDACIÓN: Verificar rejected history
                        if (idAspirant) {
                            showLoader(
                                'Verificando historial',
                                'Estamos verificando nuestro sistema por información'
                            )
                            try {
                                const response = await inscriptionsApi.verifyRejectHistory(idAspirant)
                                const rejectedHistory = response?.data?.data ?? response?.data
                                
                                // Asumimos que la respuesta es un booleano o un objeto con hasRejectedHistory
                                const hasRejected = typeof rejectedHistory === 'boolean' 
                                    ? rejectedHistory 
                                    : rejectedHistory?.hasRejectedHistory ?? false

                                // Guardar en el formulario para usar en el payload
                                if (hasRejected === false) {
                                    // Si es false, guardar que debe enviar isStatusRejectedValid: 1
                                    setValue('isStatusRejectedValid' as any, 1)
                                } else {
                                    // Si tiene rejected history, mostrar modal y no enviar el campo
                                    setValue('isStatusRejectedValid' as any, undefined)
                                    setShowRejectedHistoryModal(true)
                                }
                            } catch (error: any) {
                                console.error('❌ Error al verificar rejected history:', error)
                                // En caso de error, no establecer el campo
                                setValue('isStatusRejectedValid' as any, undefined)
                            } finally {
                                hideLoader()
                            }
                        }
                    } else {
                        console.log('📅 Periodo de inicio (valor):', value)
                        // Resetear el checkbox si no hay periodo seleccionado
                        setValue('acceptInstructiveDifferentPeriod', undefined)
                        setHasOpenedInstructiveModal(false)
                        // Resetear cuando no hay periodo seleccionado
                        setValue('isStatusRejectedValid' as any, undefined)
                    }
                }}
                rules={{
                    required: t(`${text}.required.startDateRequired`)
                }}
                isDisabled={!watchJornada || academicCycle.length === 0}
                isLoading={isLoadingCycle}
            />

            {/* Checkbox obligatorio cuando cycleId es diferente de 4, 7 o 15 */}
            {watchStartPeriod &&
                (() => {
                    const selectedCycle = academicCycle.find(
                        (cycle) =>
                            String(cycle.cycleId) === String(watchStartPeriod)
                    )
                    const cycleId = selectedCycle?.cycleId
                    const shouldShowCheckbox =
                        cycleId &&
                        ![4, 7, 15].includes(cycleId) &&
                        watchEducationModality !== '1'

                    return shouldShowCheckbox ? (
                        <div className="md:col-span-2 space-y-2">
                            <div className="flex items-start gap-2">
                                <Link
                                    as="button"
                                    type="button"
                                    onPress={() => {
                                        console.log('🔗 Abriendo modal con PDF')
                                        setIsInstructiveModalOpen(true)
                                        setHasOpenedInstructiveModal(true)
                                    }}
                                    className="text-primary cursor-pointer"
                                    showAnchorIcon
                                >
                                    Ver Instructivo de ingreso a programas
                                    diferente a enero y julio
                                </Link>
                            </div>
                            <CheckboxControl
                                name="acceptInstructiveDifferentPeriod"
                                control={control}
                                label="Aceptar Instructivo de ingreso a programas diferente a enero y julio"
                                rules={{
                                    required:
                                        'Debes leer el instructivo de ingreso para periodo diferente'
                                }}
                                isDisabled={!hasOpenedInstructiveModal}
                            />
                        </div>
                    ) : null
                })()}

            {watchStartPeriod && (
                <Tips
                    title={t(`${text}.legal`)}
                    text={t(`${text}.infoLegal`)}
                    className="md:col-span-2"
                />
            )}

{/* <SelectControl
                name="categoriaAdmision"
                control={control}
                label="¿Perteneces a alguno de estos grupos?"
                placeholder="Selecciona una opción"
                options={categoriasAdmision}
                rules={{}}
            /> */}
            {/* Modal para mostrar cuando no es elegible para programa simultáneo */}
            <AlertModal
                isOpen={showModal}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        resetModal() // Resetear cuando se cierra
                    } else {
                        setShowModal(isOpen)
                    }
                }}
                title={
                    modalTitle
                        ? t(`${text}.${modalTitle}`) || modalTitle
                        : t(`${text}.notEligibleTitle`) || 'No es elegible'
                }
                message={
                    <div className="space-y-2">
                        {modalMessage ? (
                            <p>{modalMessage}</p>
                        ) : (
                            <>
                                <p>
                                    {t(`${text}.notEligibleMessage`) ||
                                        'No puedes seleccionar este tipo de ingreso en este momento. Por favor, selecciona otro tipo de ingreso.'}
                                </p>
                                <p className="text-sm">
                                    <Link
                                        isExternal
                                        showAnchorIcon
                                        href="https://journey.debmedia.com/ubEcDjuXFSeHgd9V3oxI/16"
                                        className="text-primary"
                                    >
                                        {t(`${text}.meliLinkText`) ||
                                            'Mesa de Servicio MELI'}
                                    </Link>
                                </p>
                            </>
                        )}
                    </div>
                }
                buttonText={t('forms.ui.accept') || 'Aceptar'}
                buttonColor="primary"
                icon={<Icons.IosAlert className="w-6 h-6 text-warning" />}
            />

            {/* Modal para mostrar cuando tiene rejected history */}
            <AlertModal
                isOpen={showRejectedHistoryModal}
                onOpenChange={(isOpen) => {
                    setShowRejectedHistoryModal(isOpen)
                }}
                title="Verificación de inscripción por un experto"
                message="Tu proceso de inscripción será revisado por un experto del área de Admisiones. Muy pronto nos estaremos comunicando contigo."
                buttonText={t('forms.ui.accept') || 'Aceptar'}
                buttonColor="primary"
                icon={<Icons.IosAlert className="w-6 h-6 text-warning" />}
            />

            {/* Modal del instructivo de ingreso periodo diferente con PDF */}
            <Modal
                isOpen={isInstructiveModalOpen}
                onOpenChange={(isOpen) => {
                    setIsInstructiveModalOpen(isOpen)
                    if (isOpen) {
                        // Marcar que el modal se ha abierto para habilitar el checkbox
                        console.log('✅ Modal abierto, habilitando checkbox')
                        setHasOpenedInstructiveModal(true)
                    }
                }}
                size="5xl"
                scrollBehavior="inside"
                classNames={{
                    base: 'max-h-[90vh]',
                    body: 'p-0'
                }}
            >
                <ModalContent>
                    <ModalHeader>
                        <h2>Instructivo de Ingreso Periodo Diferente</h2>
                    </ModalHeader>
                    <ModalBody>
                        <div className="w-full h-[70vh] border rounded-lg overflow-hidden bg-white">
                            <iframe
                                src={INSTRUCTIVE_PDF_URL}
                                title="Instructivo de Ingreso Periodo Diferente"
                                className="w-full h-full"
                                style={{ border: 0 }}
                            />
                        </div>
                    </ModalBody>
                    <ModalFooter className="flex justify-between">
                        <Button
                            variant="light"
                            onPress={() => setIsInstructiveModalOpen(false)}
                        >
                            Cerrar
                        </Button>
                        <Button
                            color="primary"
                            startContent={<Icons.Download />}
                            onPress={() => {
                                // Crear un link temporal para descargar el PDF
                                const link = document.createElement('a')
                                link.href = INSTRUCTIVE_PDF_URL
                                link.download =
                                    'InstructivoInicioDiferenteEneroJulio2024_vf_2.pdf'
                                link.target = '_blank'
                                document.body.appendChild(link)
                                link.click()
                                document.body.removeChild(link)
                            }}
                        >
                            Descargar PDF
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Modal para mostrar los tipos de ingreso a la Universidad Ean */}
            {isModalTypesEntryOpen && (
                <ModalTypesEntry
                    isOpen={isModalTypesEntryOpen}
                    text={text}
                    onOpenChange={setIsModalTypesEntryOpen}
                />
            )}
        </ContainerGroupField>
    )
}
